import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, generatePath, useSearchParams } from 'react-router-dom';
import {
  PageContainerWrapper,
  TablePlus,
  CommonButton,
  GlobalLoader,
  ModalWrapper,
  NoDataFound,
  PaymentReportFilterForm,
} from '../../../components';
import { Select, Tooltip } from 'antd';
import {
  Columns,
  getCustomerOutstandingDetailService,
  sendEmail,
  markInvoicePaidService,
} from '../../../services/index.service';
import { accessRoute } from '../../../routes/Reports';
import { backToPage, modalNotification } from '../../../utils';
import { checkValidPrice } from '../../../components/UIElements/Formatter';
import { useTranslation } from 'react-i18next';
import { accessRoute as commonAccessRoutes } from '../../../routes/CommonRoutes';
import moment from 'moment';
import { selectDateData } from '../../../services/Columns';

const PAYMENT_MODE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
];

function OutstandingPaymentsDetail() {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const breadcrumb = Columns.outstandingPaymentsDetailBreadcrumb();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [state, setState] = useState({
    modal: null,
    selectedInvoice: null,
    paymentMode: 'cash',
    actionLoader: false,
  });

  // Date range filter state (mirrors Payment Report pattern)
  const [filterValue, setFilterValue] = useState({
    startDate: '',
    endDate: '',
    dateType: undefined,
    location: undefined,
  });

  const [formValues, setFormValues] = useState({
    startDate: '',
    endDate: '',
    dateType: undefined,
    location: undefined,
  });

  // Column-level filter state (provider multiselect, etc.)
  const [columnFilter, setColumnFilter] = useState({});

  const { modal, selectedInvoice, paymentMode, actionLoader } = state;

  const page = parseInt(searchParams.get('page') || 1, 10);
  const sizePerPage = parseInt(searchParams.get('pageSize') || 10, 10);
  const searchFilter = searchParams.get('search') || '';

  useEffect(() => {
    setFilterValue({
      ...filterValue,
      startDate:
        formValues?.startDate ||
        (formValues?.dateType && moment().format('YYYY-MM-DD')) ||
        '',
      endDate:
        formValues?.endDate ||
        (formValues?.dateType && moment().format('YYYY-MM-DD')) ||
        '',
      dateType: formValues?.dateType,
      location: formValues?.location,
    });
  }, [formValues]);

  const fetchDetail = async (dateFilter = filterValue, colFilter = columnFilter, currentPage = page) => {
    setLoading(true);
    try {
      const queryParams = {
        limit: sizePerPage,
        offset: (currentPage - 1) * sizePerPage,
        search: searchFilter,
        ...(dateFilter?.startDate ? { startDate: dateFilter.startDate } : {}),
        ...(dateFilter?.endDate ? { endDate: dateFilter.endDate } : {}),
        ...(dateFilter?.location ? { location: dateFilter.location } : {}),
        ...colFilter,
      };
      const response = await getCustomerOutstandingDetailService(customerId, queryParams);
      if (response?.success) {
        setCustomer(response?.data?.customer);
        setInvoices(response?.data?.invoices || []);
        setTotalCount(response?.data?.count || 0);
      }
    } catch (error) {
      console.log('Error fetching detail:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchDetail();
    }
  }, [customerId, page, sizePerPage, searchFilter]);

  // Date range filter submit handler
  const onDateFilterSubmit = (values) => {
    const newFilter = {
      startDate: values?.startDate || '',
      endDate: values?.endDate || '',
      dateType: values?.dateType,
      location: values?.location,
    };
    
    searchParams.set('page', 1);
    setSearchParams(searchParams);
    
    setFilterValue(newFilter);
    fetchDetail(newFilter, columnFilter, 1);
  };

  // Date range reset handler
  const onDateFilterReset = () => {
    const resetFilter = { startDate: '', endDate: '', dateType: undefined, location: undefined };
    
    searchParams.set('page', 1);
    setSearchParams(searchParams);
    
    setFilterValue(resetFilter);
    setFormValues({ startDate: '', endDate: '', dateType: undefined, location: undefined });
    fetchDetail(resetFilter, columnFilter, 1);
  };

  // Column-level filter handler (provider multiselect, etc.)
  const onColumnFilterChange = (newFilter) => {
    const updated = { ...columnFilter, ...newFilter };
    // Remove null/empty/undefined values
    Object.keys(updated).forEach((k) => {
      if (updated[k] === null || updated[k] === undefined || updated[k] === '') {
        delete updated[k];
        searchParams.delete(k);
      } else {
        searchParams.set(k, updated[k]);
      }
    });

    searchParams.set('page', 1);
    setSearchParams(searchParams);

    setColumnFilter(updated);
    fetchDetail(filterValue, updated, 1);
  };

  const handleSendLink = async (row) => {
    try {
      const res = await sendEmail(row?.bookingId, { email: customer?.email });
      if (res?.success) {
        modalNotification({
          type: 'success',
          message: res?.message || t('text.report.outstandingPaymentLinkSuccess'),
        });
      }
    } catch (err) {
      modalNotification({
        type: 'error',
        message: err?.message || t('text.report.outstandingPaymentLinkError'),
      });
    }
  };

  const openMarkPaidModal = (row) => {
    setState({
      ...state,
      modal: 'mark-paid',
      selectedInvoice: row,
      paymentMode: 'cash',
    });
  };

  const closeModal = () => {
    setState({ ...state, modal: null, selectedInvoice: null });
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;
    setState({ ...state, actionLoader: true });
    try {
      const res = await markInvoicePaidService({
        bookingId: selectedInvoice?.bookingId,
        paymentMode,
      });
      if (res?.success) {
        modalNotification({
          type: 'success',
          message: res?.message || t('text.report.markInvoicePaidSuccess'),
        });
        closeModal();
        fetchDetail();
      }
    } catch (err) {
      modalNotification({
        type: 'error',
        message: err?.message || t('text.report.markInvoicePaidError'),
      });
    } finally {
      setState((prev) => ({ ...prev, actionLoader: false }));
    }
  };

  const invoiceActionFormatter = ({ row }) => (
    <div className="d-flex gap-2 align-items-center">
      <Tooltip title={t('text.report.sendLink')}>
        <CommonButton
          className="btn btn-primary btn-sm ms-1"
          onClick={() => handleSendLink(row)}
        >
          {t('text.report.sendLink') || 'Send Link'}
        </CommonButton>
      </Tooltip>
      <Tooltip title={t('text.report.viewInvoice') || 'View Invoice'}>
        <CommonButton
          className="btn btn-primary btn-sm ms-1"
          onClick={() => {
            const path = generatePath(commonAccessRoutes.INVOICE.path, {
              viewInvoice: 'invoice',
              routePath: 'outstanding-payments',
              id: row?.bookingId,
            });
            window.open(path, '_blank');
          }}
        >
          {t('text.report.viewInvoice') || 'View Invoice'}
        </CommonButton>
      </Tooltip>
    </div>
  );

  const noOfPage = Math.ceil(totalCount / sizePerPage);

  const onFilterChange = (filterObj) => {
    if (filterObj && typeof filterObj === 'object') {
      Object.keys(filterObj).forEach((key) => {
        if (filterObj[key]) {
          searchParams.set(key, filterObj[key]);
        } else {
          searchParams.delete(key);
        }
      });
      searchParams.set('page', 1);
      setSearchParams(searchParams);
    }
  };

  const onFilterResetChange = () => {
    const keys = Array.from(searchParams.keys());
    keys.forEach(key => searchParams.delete(key));
    searchParams.set('page', 1);
    setSearchParams(searchParams);
    
    // Also reset all filters when "Reset All" is clicked
    const resetFilter = { startDate: '', endDate: '', dateType: undefined, location: undefined };
    setFilterValue(resetFilter);
    setFormValues({ startDate: '', endDate: '', dateType: undefined, location: undefined });
    setColumnFilter({});
    
    fetchDetail(resetFilter, {}, 1);
  };

  const activeFilters = { ...columnFilter };
  if (searchFilter) activeFilters.search = searchFilter;

  const invoiceColumns = Columns.outstandingPaymentsInvoiceTableColumn({
    page,
    sizePerPage,
    Action: invoiceActionFormatter,
    onFilterChange: onColumnFilterChange,
    filter: activeFilters,
  });

  const customerName = customer
    ? `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()
    : '';

  const phoneDisplay = customer?.phoneNumber
    ? `${customer?.countryPhoneCode ? `(+${String(customer.countryPhoneCode).replace('+', '')}) ` : ''}${customer.phoneNumber}`
    : '-';

  return (
    <>
      <PageContainerWrapper
        heading={t('text.report.customerOutstandingDetailHeading')}
        breadcrumb={breadcrumb}
        btnArray={['extraButton']}
        extraBtnText="Back"
        extraBtnClass="btn btn-outline-light bg-white"
        onExtraButtonHandleShow={() =>
          backToPage(navigate, accessRoute.OUTSTANDING_PAYMENTS.path)
        }
      >
        {(!customer && loading) ? (
          <GlobalLoader />
        ) : !customer && !loading ? (
          <NoDataFound
            source="/nodata-found.svg"
            text={t('text.report.noCustomerDetailsFound')}
          />
        ) : (
          <>
            {/* Customer Info Card */}
            {customer && (
              <div className="nk-block">
                <div className="card card-bordered mb-0">
                  <div className="card-inner">
                    <div className="row g-3">
                      <div className="col-md-6 col-xl-3">
                        <div className="user-card">
                          <div>
                            <span className="sub-text">{t('text.report.customerNameInfo')}</span>
                            <span className="lead-text">{customerName || '-'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 col-xl-3">
                        <div>
                          <span className="sub-text">{t('text.report.eMail')}</span>
                          <span className="lead-text">{customer?.email || '-'}</span>
                        </div>
                      </div>
                      <div className="col-md-6 col-xl-3">
                        <div>
                          <span className="sub-text">{t('text.report.phone')}</span>
                          <span className="lead-text">{phoneDisplay}</span>
                        </div>
                      </div>
                      <div className="col-md-6 col-xl-3">
                        <div>
                          <span className="sub-text">{t('text.report.totalOutstanding')}</span>
                          <span className="lead-text text-danger fw-bold">
                            {checkValidPrice(customer?.totalOutstanding)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Date Range Filter Bar — above the invoice table, same as Payment Report */}
            <div className="nk-block">
              <div className="card card-bordered">
                <div className="card-inner">
                  <PaymentReportFilterForm
                    onSubmit={onDateFilterSubmit}
                    onReset={onDateFilterReset}
                    setValues={setFormValues}
                    filterValue={filterValue}
                    values={formValues}
                    data={true}
                  />
                </div>
              </div>
            </div>

            {/* Outstanding Invoices Table */}
            <div className="nk-block">
              <TablePlus
                hasLimit
                noOfPage={noOfPage}
                sizePerPage={sizePerPage}
                page={page}
                count={totalCount}
                tableData={invoices}
                tableColumns={invoiceColumns}
                tableLoader={loading}
                onFilterResetChange={onFilterResetChange}
                filter={{ search: searchFilter, ...columnFilter }}
                pagination={true}
                statusAction={false}
                showAction={false}
                selectRow={true}
                header={true}
                searchField={false}
                isCard={true}
              />
            </div>
          </>
        )}
      </PageContainerWrapper>
    </>
  );
}

export default OutstandingPaymentsDetail;
